import { Entity } from "game-common/models"
import { BonecallerRitual, BONECALLER_STONE_OFFSETS } from "game-common/bonecaller_combat"
import { Graphics } from "pixi.js"
import { ScalingParams } from "../../../../client_util"

export const BONECALLER_RITUAL_EFFECT_MS = 1400
export const BONECALLER_STONES = BONECALLER_STONE_OFFSETS

/** Persistent shrine art. Transition age comes from the server, rather than replaying on entry. */
export class BonecallerShrine extends Graphics {
    private ritual?: BonecallerRitual
    private receivedAt = 0
    constructor(private coordinateScale = ScalingParams.surfaceZoom) { super() }

    update = (entity: Entity, now = Date.now()) => {
        if (entity.bonecallerRitual !== this.ritual) this.receivedAt = now
        this.ritual = entity.bonecallerRitual
        const scale = this.coordinateScale
        // Humanoid soles sit about 22px below the entity origin; the altar's centre adds another 6.
        this.position.set(entity.location.x * scale, (entity.location.y + 16) * scale)
        this.scale.set(scale)
        this.animate(now)
    }

    animate = (now = Date.now()) => {
        this.clear()
        const ritual = this.ritual
        if (!ritual) return
        const age = ritual.transitionAgeMs === undefined ? Infinity : ritual.transitionAgeMs + now - this.receivedAt
        const t = Math.min(1, age / BONECALLER_RITUAL_EFFECT_MS)
        const ending = ritual.phase !== "active"
        const collapse = ritual.phase === "interrupted"
        const glow = !ending ? 1 : Math.max(0, 1 - t)

        // An engraved altar and scattered bone fragments give the rooted caster a physical shrine.
        this.lineStyle(2, 0x686072, 0.9)
        this.beginFill(0x24212e, 0.85)
        this.drawEllipse(0, 6, 39, 24); this.endFill()
        this.lineStyle(1.5, ending ? 0x6a6171 : 0x925ab4, 0.8)
        this.drawEllipse(0, 4, 30, 17)
        for (let i = 0; i < 8; i++) {
            const a = i * Math.PI / 4
            const x = Math.cos(a) * 45, y = Math.sin(a) * 29 + 6
            this.lineStyle(2, 0xb4ada0, 0.8)
            this.moveTo(x - 3, y - 3); this.lineTo(x + 4, y + 2)
            this.drawCircle(x - 3, y - 3, 1.5)
        }
        BONECALLER_STONES.forEach((stone, index) => {
            const lit = index < ritual.stage
            const energy = lit ? glow * (0.8 + 0.2 * Math.sin(now / 200 + index)) : 0
            const x = stone.x, y = stone.y
            if (energy > 0) {
                this.lineStyle(0)
                this.beginFill(0x8436b4, energy * 0.2)
                this.drawEllipse(x, y, 18, 22); this.endFill()
                this.lineStyle(1, 0x95ec64, energy * 0.4)
                this.moveTo(x, y); this.lineTo(0, 6)
            }
        })
        if (!ending || age >= BONECALLER_RITUAL_EFFECT_MS) return
        const alpha = (1 - t) ** 2
        const radius = collapse ? 90 * (1 - t) : 18 + 140 * t
        this.lineStyle(3, collapse ? 0xb98acd : 0xb6ff75, alpha)
        this.drawEllipse(0, 6, radius, radius * 0.7)
        for (let i = 0; i < 18; i++) {
            const a = i * Math.PI / 9
            const r = collapse ? 85 * (1 - t) : 25 + 120 * t
            const x = Math.cos(a) * r, y = Math.sin(a) * r * 0.7 + 6 - Math.sin(t * Math.PI) * 24
            this.lineStyle(0)
            this.beginFill(i % 2 ? 0x9150bc : 0xd0ffa0, alpha)
            this.drawCircle(x, y, 2.5); this.endFill()
        }
    }
}

/** A single spawn's dust, cracks, and bone fragments, following the actual arrival. */
export class BonecallerEmergence extends Graphics {
    private endsAt = 0
    private sample?: number
    update = (entity: Entity, now = Date.now()) => {
        if (this.sample !== entity.bonecallerEmergenceRemainingMs) {
            this.sample = entity.bonecallerEmergenceRemainingMs
            this.endsAt = now + (this.sample || 0)
        }
        const scale = ScalingParams.surfaceZoom
        this.position.set(entity.location.x * scale, entity.location.y * scale)
        this.scale.set(scale)
        this.animate(now)
    }
    animate = (now = Date.now()) => {
        this.clear()
        const remaining = Math.max(0, this.endsAt - now)
        if (!remaining) return
        const t = 1 - remaining / 900, alpha = (1 - t) ** 2
        this.lineStyle(2, 0x9ef367, alpha)
        this.drawEllipse(0, 10, 12 + t * 25, 5 + t * 10)
        for (let i = 0; i < 6; i++) {
            const a = i * Math.PI / 3
            const x = Math.cos(a) * (8 + 20 * t), y = 10 + Math.sin(a) * 10 - Math.sin(t * Math.PI) * 25
            this.lineStyle(2, 0xe0dfc6, alpha)
            this.moveTo(x, y); this.lineTo(x + 3, y - 6)
            this.drawCircle(x + 3, y - 6, 1.5)
        }
    }
}
